import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    BooleanInput,
    required,
    email,
    useTranslate,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
    useGetIdentity,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, IconButton } from '@mui/material';
import { Person, Settings, ArrowBack, Info } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const RFID_USER_KNOWN_FIELDS = new Set([
    'vendorId', 'email', 'phone', 'firstName', 'lastName', 'displayName', 'isActive',
]);

const mapRfidUserBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const rfidUserFieldLabelKey = (field: string): string =>
    RFID_USER_KNOWN_FIELDS.has(field)
        ? `resources.rfid-users.fields.${field}`
        : field;

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/rfid-users')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.rfid-users.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.rfid-users.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const RfidUsersCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const validateEmail = email();
    const validateDisplayName = required(translate('ra.validation.required'));
    const { data: identity } = useGetIdentity();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    // Vendor-scope: vendor-scoped admins can only create users under their own
    // vendor(s). Default + restrict the vendor dropdown; pin a single-vendor
    // actor to read-only.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const defaultVendorId = isVendorScoped ? actorVendorIds[0] : undefined;

    const transform = (data: any) => {
        if (isVendorScoped && !data.vendorId) {
            return { ...data, vendorId: actorVendorIds[0] };
        }
        return data;
    };

    return (
        <Create
            title={false}
            component="div"
            transform={transform}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.rfid-users.notifications.created', { type: 'success' });
                    redirect('/rfid-users');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapRfidUserBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(rfidUserFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography variant="body2">
                            {translate('resources.rfid-users.create.infoMessage') ||
                             'RFID users are for employees or contractors who need charging access without the mobile app.'}
                        </Typography>
                    </Alert>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Person sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.rfid-users.sections.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <ReferenceInput
                                    source="vendorId"
                                    reference="vendors"
                                    label={translate('resources.rfid-users.fields.vendorId')}
                                    filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                                >
                                    <SelectInput
                                        optionText="name"
                                        validate={required()}
                                        fullWidth
                                        defaultValue={defaultVendorId}
                                        readOnly={isVendorScoped && actorVendorIds.length === 1}
                                        helperText={translate('resources.rfid-users.create.vendorHelperText') ||
                                                  'Select the vendor/company this user belongs to'}
                                    />
                                </ReferenceInput>

                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                    <TextInput
                                        source="firstName"
                                        label={translate('resources.rfid-users.fields.firstName')}
                                        fullWidth
                                    />

                                    <TextInput
                                        source="lastName"
                                        label={translate('resources.rfid-users.fields.lastName')}
                                        fullWidth
                                    />
                                </Box>

                                <TextInput
                                    source="displayName"
                                    label={translate('resources.rfid-users.fields.displayName') || 'Display Name'}
                                    validate={validateDisplayName}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.create.displayNameHelperText') ||
                                              'This name will be shown on charging sessions and reports'}
                                />

                                <TextInput
                                    source="email"
                                    label={translate('resources.rfid-users.fields.email')}
                                    validate={validateEmail}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.create.emailHelperText') ||
                                              'Optional - for notifications and receipts'}
                                />

                                <TextInput
                                    source="phone"
                                    label={translate('resources.rfid-users.fields.phone')}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.create.phoneHelperText') ||
                                              'Optional - for SMS notifications'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Settings Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.rfid-users.sections.settings') || 'Settings'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.rfid-users.fields.isActive')}
                                    defaultValue={true}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.rfid-users.create.cardInfo') || 'RFID Card Assignment'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.rfid-users.create.cardHelperText') ||
                                         'RFID cards can be assigned to this user after creation through the RFID Cards management page.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};